import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ApiError } from "@/api/errors";

import { isJobActive, type BackfillJob, type BackfillWindow } from "../lib/backfill-job";

export const BACKFILL_POLL_MS = 3000;
// ponytail: test mode polls fast so integration tests finish in ms; prod stays 3 s per spec §8.
const pollMs = import.meta.env.MODE === "test" ? 25 : BACKFILL_POLL_MS;

export const backfillJobKey = (id: string) => ["backfill-job", id] as const;
export const latestBackfillJobKey = ["backfill-jobs", "latest"] as const;

async function fetchJob(id: string): Promise<BackfillJob> {
  const { data, error } = await apiClient.GET("/v1/orders/backfill/{id}", {
    params: { path: { id } },
  });
  if (error) throw error;
  return data;
}

/**
 * POST /v1/orders/backfill — 202 returns the new job. 409 job_active means a
 * job is already running: adopt it (fetch by the returned job_id) so the UI
 * shows that job instead of an error. 503 sync_unavailable propagates as ApiError.
 */
export function useCreateBackfillJob() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silentError: true }, // SyncMenu toasts; keep the global mutation toast quiet
    mutationFn: async (window: BackfillWindow): Promise<BackfillJob> => {
      try {
        const { data, error } = await apiClient.POST("/v1/orders/backfill", { body: { window } });
        if (error) throw error;
        return data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const body = err.body as { job_id?: string } | null;
          if (body?.job_id) return fetchJob(body.job_id);
        }
        throw err;
      }
    },
    onSuccess: (job) => {
      queryClient.setQueryData(backfillJobKey(job.id), job);
      // The new job IS the latest job now — without this the freshness indicator
      // keeps serving its pre-sync value ("Never synced" / a stale timestamp)
      // next to SyncMenu's live progress until its own staleTime expires.
      void queryClient.invalidateQueries({ queryKey: latestBackfillJobKey });
    },
  });
}

/** GET /v1/orders/backfill/{id}, re-fetched every BACKFILL_POLL_MS while queued/running. */
export function useBackfillJob(id: string | null) {
  return useQuery({
    queryKey: backfillJobKey(id ?? ""),
    enabled: id !== null,
    queryFn: () => fetchJob(id ?? ""),
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data && isJobActive(query.state.data.status) ? pollMs : false,
  });
}

/** GET /v1/orders/backfill?limit=1 — newest job, or null when none has ever run. Admin-only route. */
export function useLatestBackfillJob(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: latestBackfillJobKey,
    enabled: opts.enabled ?? true,
    queryFn: async (): Promise<BackfillJob | null> => {
      const { data, error } = await apiClient.GET("/v1/orders/backfill", {
        params: { query: { limit: 1 } },
      });
      if (error) throw error;
      return data.jobs[0] ?? null;
    },
  });
}

/** POST /v1/orders/backfill/{id}/cancel — 202 with cancel_requested set; counts so far are kept. */
export function useCancelBackfillJob() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: async (id: string): Promise<BackfillJob> => {
      const { data, error } = await apiClient.POST("/v1/orders/backfill/{id}/cancel", {
        params: { path: { id } },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (job) => {
      queryClient.setQueryData(backfillJobKey(job.id), job);
      // Cancelling changes what the "latest job" reflects too.
      void queryClient.invalidateQueries({ queryKey: latestBackfillJobKey });
    },
  });
}
